// Feedback and complaints (Feedback.view — the Compliance Manager): every piece of
// feedback from the public form and from staff, filtered on the page, opened in
// components/FeedbackDialog.jsx to be worked. The rules — filters and their search —
// are feedbackRules.js; the export takes the rows exactly as filtered.
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputAdornment, InputLabel, Link, MenuItem, Paper, Select,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SearchIcon from "@mui/icons-material/Search";
import { API_BASE_URL } from "../config";
import {
  FEEDBACK_NATURE_COLOR, FEEDBACK_RESOLUTIONS, FEEDBACK_STATUSES, STAFF_FEEDBACK_NATURES,
  FEEDBACK_STATUS_COLOR,
} from "../constants";
import {
  NO_RESOLUTION, OTHER_EVENT, RESPONSE_OUTSTANDING, RESPONSE_OVERDUE, emptyFeedbackFilters,
  feedbackEventText, filterFeedback, responseState,
} from "../feedbackRules";
import { exportFeedback } from "../exportExcel";
import { downloadQrCode } from "../qrCode";
import FeedbackDialog from "../components/FeedbackDialog";

const API = `${API_BASE_URL}/feedback`;

// The public form's address, as the QR code encodes it. Taken from wherever the app
// is being used, so the code printed from the live site points at the live form —
// and the dialog shows it before anything downloads, because one printed from a
// local copy would point at localhost.
const publicFormUrl = () => `${window.location.origin}/submit-feedback`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

// The due date of a Complaint's or Speaking Up's formal response: red once overdue,
// plain while due, greyed once Closed. Blank for feedback that needs none.
function ResponseDue({ fb }) {
  const r = responseState(fb);
  if (!r) return null;
  const date = r.due.toLocaleDateString();
  if (r.state === "overdue") return <Chip size="small" color="error" label={`Overdue — ${date}`} />;
  return (
    <Typography variant="body2" color={r.state === "closed" ? "text.secondary" : "text.primary"}>
      {date}
    </Typography>
  );
}

function QrDialog({ open, onClose }) {
  const url = publicFormUrl();
  const [saving, setSaving] = useState(false);
  const local = /localhost|127\.0\.0\.1/.test(url);
  const save = () => {
    setSaving(true);
    downloadQrCode({ name: "shelter-stone-feedback", url }).finally(() => setSaving(false));
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>QR code for the public feedback form</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">
            Print or publish this code so participants and members of the public can give
            feedback or make a complaint without signing in. It opens:
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            <Link href={url} target="_blank" rel="noopener">{url}</Link>
          </Typography>
          {local && (
            <Alert severity="warning">
              This is a local address — a code made here won't work for anyone else. Download it
              from the live site instead.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<QrCode2Icon />} onClick={save} disabled={saving}>
          Download PNG
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Feedback() {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState(emptyFeedbackFilters);
  const [openId, setOpenId] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/`).then(res => setRows(res.data)).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  // The events that have feedback, most recent first (the rows already are).
  const events = useMemo(() => {
    const seen = new Map();
    for (const r of rows || []) {
      if (r.event_id != null && !seen.has(r.event_id)) seen.set(r.event_id, r.event_label);
    }
    return [...seen.entries()];
  }, [rows]);

  const shown = useMemo(() => filterFeedback(rows || [], filters), [rows, filters]);
  const set = (key) => (e) => setFilters(f => ({ ...f, [key]: e.target.value }));
  const filtered = Object.values(filters).some(Boolean);

  const replace = (fb) => setRows(rs => rs.map(r => (r.id === fb.id ? fb : r)));

  const select = (key, label, options, width = 170) => (
    <FormControl size="small" sx={{ minWidth: width }}>
      <InputLabel id={`fbf-${key}`}>{label}</InputLabel>
      <Select labelId={`fbf-${key}`} label={label} value={filters[key]} onChange={set(key)}>
        <MenuItem value=""><em>Any</em></MenuItem>
        {options.map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h4" sx={{ flex: 1 }}>Feedback</Typography>
        <Button variant="outlined" startIcon={<QrCode2Icon />} onClick={() => setQrOpen(true)}>
          Public form QR code
        </Button>
        <Button variant="outlined" startIcon={<FileDownloadIcon />} disabled={!shown.length}
                onClick={() => exportFeedback(shown)}>
          Export to Excel
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        {select("event", "Event",
          [...events.map(([id, label]) => [String(id), label]), [OTHER_EVENT, "Not a listed event"]], 220)}
        {select("nature", "Type", STAFF_FEEDBACK_NATURES.map(n => [n, n]))}
        {select("status", "Status", FEEDBACK_STATUSES.map(s => [s, s]))}
        {select("response", "Response required", [
          [RESPONSE_OUTSTANDING, "Outstanding"], [RESPONSE_OVERDUE, "Overdue"]], 190)}
        {select("resolution", "Resolution",
          [...FEEDBACK_RESOLUTIONS.map(r => [r, r]), [NO_RESOLUTION, "None yet"]], 200)}
        <TextField size="small" label="Search names" value={filters.search} onChange={set("search")}
                   slotProps={{ input: { startAdornment: (
                     <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) } }}
                   helperText="Who sent it, who it's on behalf of, or the linked entrant"
                   sx={{ minWidth: 260 }} />
        {filtered && <Button size="small" onClick={() => setFilters(emptyFeedbackFilters())}>Clear</Button>}
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {rows ? `${shown.length} of ${rows.length}` : ""}
        </Typography>
      </Box>

      {rows == null ? <CircularProgress /> : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ref</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Response due</TableCell>
                <TableCell>Resolutions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary">
                      {rows.length ? "Nothing matches these filters." : "No feedback yet."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {shown.map(fb => (
                <TableRow hover key={fb.id} onClick={() => setOpenId(fb.id)} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fb.reference}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(fb.submitted_at)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {(fb.nature || []).map(n => (
                        <Chip key={n} size="small" label={n} color={FEEDBACK_NATURE_COLOR[n] || "default"} />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{feedbackEventText(fb)}</TableCell>
                  <TableCell>
                    {fb.submitter_name || <em>No name</em>}
                    {fb.source === "staff" && (
                      <Typography component="span" variant="caption" color="text.secondary"> (staff)</Typography>
                    )}
                    {fb.on_behalf_of && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        for {fb.on_behalf_of}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap>{fb.details}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={fb.status} color={FEEDBACK_STATUS_COLOR[fb.status] || "default"} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}><ResponseDue fb={fb} /></TableCell>
                  <TableCell>{(fb.resolutions || []).join(", ")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <FeedbackDialog feedbackId={openId} onClose={() => setOpenId(null)} onChanged={replace}
                      onDeleted={(id) => setRows(rs => rs.filter(r => r.id !== id))} />
      <QrDialog open={qrOpen} onClose={() => setQrOpen(false)} />
    </Stack>
  );
}
